import React, { useState, useEffect, useMemo } from "react";
import { CustomModal } from "../../../../components/modal";
import {
  GlobalActionKey,
  ACTION_VALUE_TYPES,
  getAvailableActionKeys,
} from "../domain/globalAction.types";
import { isValidUrl } from "../infrastructure/globalActionsMapper";
import { Button } from "react-bootstrap";
import { useEnumOptions } from "../hooks/useEnumOptions";
import { FormattedMessage, useIntl } from "react-intl";
import { actionModalMessages, useActionLabels } from "../../orchestrator.messages";
import { useProperty } from "../../../../components/property-provider";
import { SegmentGrpcRepository } from "../../segment/infrastructure/repositories/SegmentGrpcRepository";
import { useSegments } from "../../segment/application/hooks/useSegments";
import { BooleanInput } from "./action/BooleanInput";
import { EnumInput } from "./action/EnumInput";
import { SegmentInput } from "./action/SegmentInput";
import { StringListInput } from "./action/StringListInput";
import { UrlInput } from "./action/UrlInput";
import { StringInput } from "./action/StringInput";

const segmentRepository = new SegmentGrpcRepository();

interface AddActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (key: GlobalActionKey, value: string) => void;
  editingKey?: GlobalActionKey;
  editingValue?: string;
  existingKeys: string[];
}

export function AddActionModal({
  isOpen,
  onClose,
  onSave,
  editingKey,
  editingValue,
  existingKeys,
}: AddActionModalProps) {
  const intl = useIntl();
  const actionLabels = useActionLabels();
  const propertyInfo = useProperty();
  const [selectedKey, setSelectedKey] = useState<GlobalActionKey | "">("");
  const [inputValue, setInputValue] = useState<string>("");
  const [boolValue, setBoolValue] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const { getEnumOptions } = useEnumOptions();

  const segmentsEnabled = isOpen && (selectedKey === "segment" || editingKey === "segment");
  const { data: segments = [], isFetching: loadingSegments } = useSegments(
    propertyInfo.ticker,
    segmentRepository,
    { enabled: segmentsEnabled }
  );

  const availableKeys = useMemo(() => {
    return getAvailableActionKeys(existingKeys).filter(
      key => key !== editingKey || editingKey !== undefined
    );
  }, [existingKeys, editingKey]);

  useEffect(() => {
    if (isOpen) {
      setError("");
      if (editingKey && editingValue !== undefined) {
        setSelectedKey(editingKey);
        const valueType = ACTION_VALUE_TYPES[editingKey];
        if (valueType === "boolean") {
          setBoolValue(editingValue.toLowerCase() === "true");
          setInputValue("");
        } else {
          setInputValue(editingValue);
          setBoolValue(false);
        }
      } else {
        setSelectedKey(availableKeys[0] || "");
        setInputValue("");
        setBoolValue(false);
      }
    }
  }, [isOpen, editingKey, editingValue, availableKeys]);

  useEffect(() => {
    if (segmentsEnabled && !editingValue && segments.length > 0 && !inputValue) {
      setInputValue(segments[0].ticker);
    }
  }, [segmentsEnabled, editingValue, segments, inputValue]);

  useEffect(() => {
    if (!editingKey && selectedKey) {
      const valueType = ACTION_VALUE_TYPES[selectedKey];
      if (valueType === "boolean") {
        setBoolValue(false);
        setInputValue("");
      } else if (valueType === "enum") {
        const options = getEnumOptions(selectedKey);
        setInputValue(options[0]?.value || "");
      } else {
        setInputValue("");
      }
      setError("");
    }
  }, [selectedKey, editingKey, getEnumOptions]);

  const validate = (): boolean => {
    if (!selectedKey) {
      return false;
    }

    const valueType = ACTION_VALUE_TYPES[selectedKey];

    if (valueType === "url" && inputValue && !isValidUrl(inputValue)) {
      setError(intl.formatMessage(actionModalMessages.invalidURLFormat));
      return false;
    }

    setError("");
    return true;
  };

  const handleSave = () => {
    if (!selectedKey || !validate()) {
      return;
    }

    const valueType = ACTION_VALUE_TYPES[selectedKey];
    let finalValue: string;

    if (valueType === "boolean") {
      finalValue = boolValue ? "true" : "false";
    } else {
      finalValue = inputValue;
    }

    onSave(selectedKey, finalValue);
  };

  const renderValueInput = () => {
    if (!selectedKey) {
      return null;
    }

    if (selectedKey === "segment") {
      return (
        <SegmentInput
          value={inputValue}
          segments={segments}
          loading={loadingSegments}
          onChange={setInputValue}
        />
      );
    }

    switch (ACTION_VALUE_TYPES[selectedKey]) {
      case "boolean":
        return <BooleanInput value={boolValue} onChange={setBoolValue} />;
      case "enum":
        return (
          <EnumInput
            value={inputValue}
            options={getEnumOptions(selectedKey)}
            onChange={setInputValue}
          />
        );
      case "stringList":
        return <StringListInput value={inputValue} onChange={setInputValue} />;
      case "url":
        return <UrlInput value={inputValue} error={error} onChange={setInputValue} />;
      case "string":
      default:
        return <StringInput value={inputValue} onChange={setInputValue} />;
    }
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        editingKey
          ? intl.formatMessage(actionModalMessages.editActionTitle)
          : intl.formatMessage(actionModalMessages.addActionTitle)
      }
      size="lg">
      <div className="space-y-4 p-4">
        <div>
          <label className="block font-medium text-gray-700 mb-1">
            <FormattedMessage {...actionModalMessages.actionType} />
          </label>
          <select
            value={selectedKey}
            onChange={e => setSelectedKey(e.target.value as GlobalActionKey)}
            disabled={!!editingKey}
            className="w-full border rounded px-3 py-2 disabled:bg-gray-100">
            {!editingKey && (
              <option value="">
                <FormattedMessage {...actionModalMessages.selectAction} />
              </option>
            )}
            {(editingKey ? [editingKey] : availableKeys).map(key => (
              <option key={key} value={key}>
                {actionLabels[key]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block font-medium text-gray-700 mb-1">
            <FormattedMessage {...actionModalMessages.value} />
          </label>
          {renderValueInput()}
        </div>

        {error && ACTION_VALUE_TYPES[selectedKey as GlobalActionKey] !== "url" && (
          <div className="text-red-500 text-sm">{error}</div>
        )}

        <div className="flex justify-start gap-2 pt-4">
          <Button
            className="px-4 py-2"
            variant="primary"
            type="button"
            disabled={!selectedKey}
            onClick={handleSave}>
            {editingKey
              ? intl.formatMessage(actionModalMessages.update)
              : intl.formatMessage(actionModalMessages.add)}
          </Button>
          <Button className="px-4 py-2" variant="light" type="button" onClick={onClose}>
            <FormattedMessage {...actionModalMessages.cancel} />
          </Button>
        </div>
      </div>
    </CustomModal>
  );
}
